/**
 * Image optimization utilities for responsive, optimized images
 */

interface ImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'auto';
}

/**
 * Get an optimized image URL with size and quality parameters
 */
export function getOptimizedImageUrl(url: string, options: ImageOptions = {}): string {
  if (!url) return '/placeholder.svg';
  
  const { width = 800, quality = 80 } = options;
  
  // For Supabase Storage URLs - add transform parameters
  if (url.includes('supabase.co/storage')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}&quality=${quality}`;
  }
  
  // For Unsplash URLs - they support w and q parameters
  if (url.includes('unsplash.com')) {
    let optimizedUrl = url;
    // Update or add width
    if (url.includes('w=')) {
      optimizedUrl = optimizedUrl.replace(/w=\d+/, `w=${width}`);
    } else {
      optimizedUrl += url.includes('?') ? `&w=${width}` : `?w=${width}`;
    }
    // Update or add quality
    if (url.includes('q=')) {
      optimizedUrl = optimizedUrl.replace(/q=\d+/, `q=${quality}`);
    } else {
      optimizedUrl += `&q=${quality}`;
    }
    return optimizedUrl;
  }
  
  // For other URLs, return as-is
  return url;
}

/**
 * Generate srcset for responsive images
 */
export function generateSrcSet(url: string, sizes: number[] = [320, 640, 960, 1280]): string {
  if (!url) return '';
  
  return sizes
    .map(size => `${getOptimizedImageUrl(url, { width: size })} ${size}w`)
    .join(', ');
}

/**
 * Get appropriate sizes attribute for common use cases
 */
export function getImageSizes(variant: 'card' | 'hero' | 'thumbnail' | 'full' = 'card'): string {
  switch (variant) {
    case 'hero':
      return '100vw';
    case 'thumbnail':
      return '(max-width: 640px) 80px, 120px';
    case 'full':
      return '(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px';
    case 'card':
    default:
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
  }
}
